/**
 * StoreGate — decides what an APPROVED merchant with no active store sees.
 *
 * Three outcomes, and the distinction between the last two is the whole point:
 *
 *   • The merchant has stores        → pick one.
 *   • The merchant has none          → create the first one.
 *   • We could not ask the server    → retry. NOT "create one".
 *
 * That third branch matters. Falling through to the setup form when
 * `check-status` merely failed would invite an offline merchant to create a
 * second store that duplicates the one they already have — and because stores
 * carry their own IndexedDB and their own invoice sequence, the duplicate is
 * not a cosmetic problem: sales would accumulate in a store the rest of the
 * business cannot see.
 *
 * Reached on two paths: straight after approval, and after a reload that
 * restored a session whose store selection never completed.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { registerDevice } from '@/services/deviceIdentity';
import { checkStatus } from '@/auth/onboardingApi';
import type { StoreSummary } from '@/auth/authApi';
import { BrandLoader } from '@/components/BrandLoader';
import { StoreSetupScreen } from './StoreSetupScreen';
import { OnboardingCard, PRIMARY, QUIET } from './OnboardingCard';

type State =
  | { phase: 'loading' }
  | { phase: 'pick'; stores: StoreSummary[] }
  | { phase: 'setup' }
  | { phase: 'error'; message: string };

export function StoreGate({ onLogout }: { onLogout: () => void }) {
  const setActiveStore = useAppStore((s) => s.setActiveStore);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [busy, setBusy] = useState(false);

  // Declared before `load` so the single-store fast path can call it. Stable
  // across renders because `load` lists it as a dependency.
  const choose = useCallback(
    async (storeId: string) => {
      setBusy(true);
      try {
        // Register before activating, for the reason useAuth documents: setting
        // activeStoreId reveals the shell, which immediately syncs, and the
        // backend allocates the slot by UPDATING a device row that must already
        // exist — otherwise the first pull 503s. Best-effort: an unregistered
        // browser can still bill locally, and blocking a cashier over a
        // registration hiccup would be worse.
        try {
          await registerDevice();
        } catch (err) {
          console.warn('[onboarding] device registration failed; continuing', err);
        }
        await setActiveStore(storeId);
      } finally {
        setBusy(false);
      }
    },
    [setActiveStore],
  );

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await checkStatus();

      // This response is the authority on onboarding status, and writing it
      // back is what resolves a session persisted before the status was
      // stored: App.tsx routes such a session here precisely so the server can
      // answer, and a non-APPROVED answer re-renders the gate into the right
      // screen instead of offering stores the merchant may not use yet.
      const serverStatus = res.user?.onboardingStatus;
      const user = useAppStore.getState().currentUser;
      if (user && serverStatus && serverStatus !== user.onboardingStatus) {
        await setCurrentUser({ ...user, onboardingStatus: serverStatus });
        if (serverStatus !== 'APPROVED') return;
      }

      const stores = res.stores ?? [];

      // One store is not a choice. Selecting it automatically is what the
      // mobile app does, and it keeps the common case — a single-counter shop
      // reopening its till — at zero clicks after the OTP.
      if (stores.length === 1) {
        await choose(stores[0].id);
        return;
      }

      setState(stores.length > 0 ? { phase: 'pick', stores } : { phase: 'setup' });
    } catch (err) {
      setState({
        phase: 'error',
        message:
          err instanceof TypeError
            ? 'Cannot reach the server. Check the connection and try again.'
            : ((err as Error)?.message ?? 'Could not load your stores.'),
      });
    }
  }, [setCurrentUser, choose]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') return <BrandLoader label="Loading your stores…" />;
  if (state.phase === 'setup') return <StoreSetupScreen onLogout={onLogout} />;

  if (state.phase === 'error') {
    return (
      <OnboardingCard
        title="Could not load your stores"
        error={state.message}
        footer={
          <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
            Sign out
          </button>
        }
      >
        <button type="button" onClick={() => void load()} className={PRIMARY}>
          Try again
        </button>
      </OnboardingCard>
    );
  }

  return (
    <OnboardingCard
      title="Choose a store"
      subtitle="Pick the counter you're billing for."
      footer={
        <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
          Sign out
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        {state.stores.map((s) => (
          <button
            key={s.id}
            type="button"
            disabled={busy}
            onClick={() => void choose(s.id)}
            className="rounded-lg border border-white/10 bg-white/[0.06] p-4 text-left text-body text-fg-primary backdrop-blur-sm transition-colors duration-120 ease-apple hover:border-white/25 hover:bg-white/[0.12] disabled:opacity-40"
          >
            {s.name}
          </button>
        ))}
      </div>
    </OnboardingCard>
  );
}
